import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { EProjectStatus } from '@/enums';
import useOrganizationRoles from '@/hooks/useOrganizationRoles';
import useTranslation from '@/hooks/useTranslation';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import { useMutateApprovePublishingProject } from '@/reactQuery/post';
import CompleteProjectModal from '../Modals/CompleteProjectModal';
import RequestPublishingModal from '../Modals/RequestPublishingModal';
import UnpublishProjectModal from '../Modals/UnpublishProjectModal';
import RepublishProjectModal from '../Modals/RepublishProjectModal';
import { Button } from '../ui/button';
import useProjectRequiredFieldsStore from '@/store/ProjectRequiredFieldsStore';
import getHTMLTextOnPublishButtonHover from '@/utils/getHTMLTextOnPublishButtonHover';
import PublishProjectModal from '../Modals/PublishProjectModal';
import { useCurrentProject } from '@/hooks/useCurrentProject';

function ProjectButtonScenarios() {
  const {
    project_id: projectId,
    workspace_id: workspaceId,
    organization_id: organizationId,
  } = useParams();
  const { lang } = useTranslation();

  const { emptyFieldsLabels } = useProjectRequiredFieldsStore();
  const [publishTooltipText, setPublishTooltipText] = useState<string>('');
  const [openUnpublishModal, setOpenUnpublishModal] = useState(false);
  const [openRepublishModal, setOpenRepublishModal] = useState(false);
  const {
    isUserAdmin: isUserAdminOrg,
    isUserEditor: isUserEditorOrg,
    isUserOwner: isUserOwnerOrg,
  } = useOrganizationRoles();
  const { currentProject: singleProjectData } = useCurrentProject();
  const { isUserViewer: isUserViewerWorkspace } = useWorkspaceRoles();

  // -- helpers --

  // statuses
  const isStatusDraft: boolean = singleProjectData?.status === EProjectStatus.DRAFT;
  const isStatusReady: boolean = singleProjectData?.status === EProjectStatus.READY;
  const isStatusRequestApproved: boolean =
    singleProjectData?.status === EProjectStatus.REQUEST_APPROVED;
  const isStatusWaitingApproval: boolean =
    singleProjectData?.status === EProjectStatus.WAITING_APPROVAL;
  const isStatusPublished: boolean = singleProjectData?.status === EProjectStatus.PUBLISHED;
  const isStatusCompleted: boolean = singleProjectData?.status === EProjectStatus.COMPLETED;

  // role groups
  const IS_ORGANIZATION_OWNER_OR_ADMIN: boolean = isUserOwnerOrg || isUserAdminOrg;

  // -- BUTTON SCENARIOS --
  const PUBLISH_SCENARIO: boolean =
    ((isStatusDraft || isStatusReady || isStatusRequestApproved) &&
      IS_ORGANIZATION_OWNER_OR_ADMIN) ||
    (isUserEditorOrg && isStatusRequestApproved);

  const REQUEST_TO_PUBLISH_SCENARIO: boolean = isUserEditorOrg && (isStatusReady || isStatusDraft);

  const REQUEST_SENT_SCENARIO: boolean = isUserEditorOrg && isStatusWaitingApproval;

  const APPROVE_PUBLISHING_SCENARIO: boolean = IS_ORGANIZATION_OWNER_OR_ADMIN && !PUBLISH_SCENARIO;

  const PROJECT_PUBLISHED_SCENARIO: boolean = isStatusPublished;

  const PROJECT_COMPLETED_SCENARIO: boolean = isStatusCompleted;

  // FUNCTIONS

  const { mutateAsync: mutateAsyncApprovePublishingProject } = useMutateApprovePublishingProject(
    organizationId,
    workspaceId,
  );

  const approvePublishingProjectFunction = async (formData: string | undefined) => {
    try {
      await mutateAsyncApprovePublishingProject(formData);
      toast.success(lang.get('msg.projectSuccessfullyApproved'));
    } catch (error) {
      toast.error('Error. Please try agian.');
    }
  };

  useEffect(() => {
    setPublishTooltipText(getHTMLTextOnPublishButtonHover(emptyFieldsLabels, lang));
  }, [emptyFieldsLabels, lang]);

  return (
    <div className="flex items-center justify-end w-fit">
      {/* PUBLISH */}
      {PUBLISH_SCENARIO && !PROJECT_PUBLISHED_SCENARIO && (
        <PublishProjectModal
          isProjectInDraftStatus={isStatusDraft}
          projectTitle={singleProjectData?.title}
          projectId={projectId}
          isPublishRequestApproved={isStatusRequestApproved}
          publishTooltipText={publishTooltipText}
          canPublish={PUBLISH_SCENARIO}
          isWorkspaceViewer={isUserViewerWorkspace}
        />
      )}

      {/* PROJECT PUBLISHED SCENARIO */}
      {PROJECT_PUBLISHED_SCENARIO && (
        <div className="flex items-center gap-3">
          <Button
            variant="default"
            disabled
            type="button"
            className="h-8 px-5"
            data-testid="project-published-button"
          >
            {lang.get('msg.projectPublished')}
          </Button>

          {/* Unpublish button - only for org owners/admins/editors, disabled for workspace viewers */}
          {(IS_ORGANIZATION_OWNER_OR_ADMIN || isUserEditorOrg) && (
            <Button
              variant="outline"
              type="button"
              className="h-8 px-5"
              onClick={() => setOpenUnpublishModal(true)}
              disabled={isUserViewerWorkspace}
              data-testid="unpublish-button"
            >
              {lang.get('msg.unpublish')}
            </Button>
          )}

          <CompleteProjectModal />
        </div>
      )}

      {/* Unpublish Modal */}
      {PROJECT_PUBLISHED_SCENARIO && (IS_ORGANIZATION_OWNER_OR_ADMIN || isUserEditorOrg) && (
        <UnpublishProjectModal
          projectId={projectId || ''}
          projectTitle={singleProjectData?.title || ''}
          organizationId={organizationId}
          workspaceId={workspaceId}
          isOpen={openUnpublishModal}
          onClose={() => setOpenUnpublishModal(false)}
        />
      )}

      {/* REQUEST TO PUBLISH */}
      {REQUEST_TO_PUBLISH_SCENARIO && (
        <RequestPublishingModal
          requestAgain={false}
          isStatusDraft={isStatusDraft}
          isWorkspaceViewer={isUserViewerWorkspace}
        />
      )}

      {/* APPROVE PUBLISHING */}
      {APPROVE_PUBLISHING_SCENARIO && isStatusWaitingApproval && (
        <Button
          variant="default"
          onClick={() => approvePublishingProjectFunction(projectId)}
          disabled={isUserEditorOrg && isUserViewerWorkspace}
          type="button"
          className="h-8 px-5"
          data-testid="approve-publishing-button"
        >
          {lang.get('msg.approvePublishing')}
        </Button>
      )}

      {APPROVE_PUBLISHING_SCENARIO && isStatusRequestApproved && (
        <Button
          variant="default"
          disabled
          type="button"
          className="h-8 px-5"
          data-testid="approved-button"
        >
          {lang.get('msg.approved')}
        </Button>
      )}

      {PROJECT_COMPLETED_SCENARIO && (
        <div className="flex items-center gap-3">
          <Button
            variant="default"
            disabled
            type="button"
            className="h-8 px-5"
            data-testid="completed-button"
          >
            {lang.get('msg.completed')}
          </Button>

          {/* Re-publish button - only for org owners/admins/editors, disabled for workspace viewers */}
          {(IS_ORGANIZATION_OWNER_OR_ADMIN || isUserEditorOrg) && (
            <Button
              variant="outline"
              type="button"
              className="h-8 px-5"
              onClick={() => setOpenRepublishModal(true)}
              disabled={isUserViewerWorkspace}
              data-testid="republish-button"
            >
              {lang.get('msg.republish')}
            </Button>
          )}
        </div>
      )}

      {/* Republish Modal */}
      {PROJECT_COMPLETED_SCENARIO && (IS_ORGANIZATION_OWNER_OR_ADMIN || isUserEditorOrg) && (
        <RepublishProjectModal
          projectId={projectId || ''}
          projectTitle={singleProjectData?.title || ''}
          organizationId={organizationId}
          workspaceId={workspaceId}
          isOpen={openRepublishModal}
          onClose={() => setOpenRepublishModal(false)}
        />
      )}

      {/* REQUEST SENT + REQUEST AGAIN */}
      {REQUEST_SENT_SCENARIO && (
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            disabled
            type="button"
            className="h-8 px-5"
            data-testid="request-sent-button"
          >
            {lang.get('msg.requestSent')}
          </Button>
          {!isUserViewerWorkspace && (
            <RequestPublishingModal requestAgain isStatusDraft={isStatusDraft} />
          )}
        </div>
      )}
    </div>
  );
}

export default ProjectButtonScenarios;
